import { useEffect, useRef, useState } from "react";

/**
 * Lit une valeur persistée dans localStorage de façon défensive.
 * - Retourne `fallback` si la clé est `null` (ex: facility pas encore connue), en SSR/test,
 *   ou si le contenu est absent / corrompu.
 */
export function readLocalStorage<T>(key: string | null, fallback: T): T {
    if (key === null || typeof window === "undefined") return fallback;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return fallback;
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

/** Écrit une valeur dans localStorage en ignorant les erreurs (quota / mode privé). */
export function writeLocalStorage<T>(key: string, value: T): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore quota / private mode
    }
}

/**
 * `useState` persisté dans localStorage, avec une clé éventuellement dynamique.
 *
 * La clé peut être `null` (ex: tant que la facility courante n'est pas chargée) :
 * dans ce cas aucune lecture/écriture n'a lieu et l'état reste sur `initialValue`.
 *
 * Quand la clé change (facility chargée tardivement, ou admin de groupe qui switche
 * de facility), l'état est **relu** depuis la nouvelle clé — un `useState(() => read())`
 * naïf ne le ferait pas et écraserait la valeur d'une autre facility.
 *
 * Note: la validation de forme (champs attendus, tableaux) doit être faite par l'appelant
 * en fusionnant la valeur lue sur ses défauts — ce hook générique ne peut pas valider `T`.
 */
export function useLocalStorageState<T>(
    key: string | null,
    initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [state, setState] = useState<T>(() => readLocalStorage(key, initialValue));
    const keyRef = useRef(key);

    // Re-lecture si la clé change (déclaré AVANT l'effet d'écriture pour un ordre déterministe).
    useEffect(() => {
        if (keyRef.current !== key) {
            keyRef.current = key;
            setState(readLocalStorage(key, initialValue));
        }
        // `initialValue` volontairement hors deps : on ne veut relire que sur changement de clé.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    // Écriture — jamais sous une clé `null`.
    useEffect(() => {
        if (key !== null) writeLocalStorage(key, state);
    }, [key, state]);

    return [state, setState];
}
